// Fichier: src/pages/EvaluationList.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  orderBy,
  query,
} from 'firebase/firestore';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Collapse,
  IconButton,
  LinearProgress,
  Tooltip,
  MenuItem,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import moment from 'moment';
import 'moment/locale/fr';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const SCORE_COLOR_MUI = (score) => {
  if (score >= 8) return 'success';
  if (score >= 5) return 'primary';
  return 'error';
};

const SCORE_HEX = (score) => {
  if (score >= 8) return '#16a34a';
  if (score >= 5) return '#7C3AED';
  return '#dc2626';
};

const PERIOD_OPTIONS = [
  { value: 'all',     label: 'Toutes les périodes' },
  { value: 'month',   label: 'Ce mois-ci' },
  { value: 'quarter', label: 'Ce trimestre' },
  { value: 'season',  label: 'Cette saison (sept → juin)' },
];

const isInPeriod = (date, period) => {
  if (!date || period === 'all') return true;
  const m = moment(date);
  if (period === 'month')   return m.isSame(moment(), 'month');
  if (period === 'quarter') return m.isSame(moment(), 'quarter');
  if (period === 'season') {
    const now  = moment();
    const year = now.month() >= 8 ? now.year() : now.year() - 1;
    return m.isBetween(`${year}-09-01`, `${year + 1}-06-30`, 'day', '[]');
  }
  return true;
};

// ─── Carte d'une évaluation ───────────────────────────────────────────────────
const EvaluationCard = ({ evaluation, profileId, onDelete }) => {
  const navigate  = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const avg =
    evaluation.criteria?.length > 0
      ? (evaluation.criteria.reduce((s, c) => s + (c.score ?? 0), 0) / evaluation.criteria.length).toFixed(1)
      : null;

  const dateLabel = evaluation.date?.toDate
    ? moment(evaluation.date.toDate()).format('DD MMMM YYYY')
    : '—';

  return (
    <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, borderColor: '#E5E7EB', '&:hover': { borderColor: '#7C3AED44' } }}>
      <CardContent sx={{ pb: '12px !important' }}>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              {/* Titre */}
              <Typography sx={{ fontWeight: 'bold', fontSize: 15 }}>
                {evaluation.title || dateLabel}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {dateLabel}
              </Typography>
              {avg && (
                <Chip
                  label={`Moy. ${avg}/10`}
                  color={SCORE_COLOR_MUI(Number(avg))}
                  size="small"
                  variant="outlined"
                />
              )}
              <Chip
                label={`${evaluation.criteria?.length || 0} critère${evaluation.criteria?.length > 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
                sx={{ color: 'text.secondary' }}
              />
            </Box>

            {/* Aperçu mini barres */}
            <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, flexWrap: 'wrap' }}>
              {(evaluation.criteria || []).map((c) => (
                <Tooltip key={c.key} title={`${c.label} : ${c.score}/10`} arrow>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 32 }}>
                    <Box sx={{ width: 32, height: 6, borderRadius: 1, bgcolor: 'grey.100', overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', width: `${(c.score / 10) * 100}%`, bgcolor: SCORE_HEX(c.score), borderRadius: 1 }} />
                    </Box>
                    <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary', mt: 0.2 }}>
                      {c.score}
                    </Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>

            {/* Préview commentaire global */}
            {!expanded && evaluation.globalComment && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 480 }}>
                {evaluation.globalComment}
              </Typography>
            )}
          </Box>

          {/* Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
            <Tooltip title="Modifier">
              <IconButton size="small" onClick={() => navigate(`/student-evolutions/${profileId}/evaluations/edit/${evaluation.id}`)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Supprimer">
              <IconButton size="small" color="error" onClick={() => onDelete(evaluation)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Box>
        </Box>

        {/* Détail dépliable */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
              {(evaluation.criteria || []).map((c) => (
                <Box key={c.key} sx={{ p: 1.5, border: '1px solid #F3F4F6', borderRadius: 2, bgcolor: '#FAFAFA' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{c.label}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: SCORE_HEX(c.score) }}>
                      {c.score}/10
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={c.score * 10}
                    sx={{ height: 6, borderRadius: 1, bgcolor: 'grey.100', '& .MuiLinearProgress-bar': { bgcolor: SCORE_HEX(c.score) } }}
                  />
                  {c.comment && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {c.comment}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>

            {evaluation.globalComment && (
              <Box sx={{ p: 2, bgcolor: '#F5F3FF', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ color: '#7C3AED', mb: 0.5 }}>Commentaire global</Typography>
                <Typography variant="body2">{evaluation.globalComment}</Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// ─── Composant principal ──────────────────────────────────────────────────────
const EvaluationList = ({ profileId }) => {
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [period, setPeriod]           = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    fetchEvaluations();
  }, [profileId]);

  const fetchEvaluations = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'student_evolution_profiles', profileId, 'evaluations'),
        orderBy('date', 'desc')
      );
      const snap = await getDocs(q);
      setEvaluations(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Erreur chargement évaluations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, 'student_evolution_profiles', profileId, 'evaluations', deleteTarget.id));
      setEvaluations((prev) => prev.filter((e) => e.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error('Erreur suppression évaluation:', err);
    }
  };

  const filtered = evaluations.filter((e) => isInPeriod(e.date?.toDate?.(), period));

  // Synthèse moyennes par critère
  const globalStats = (() => {
    const allCriteria = filtered.flatMap((e) => e.criteria || []);
    if (!allCriteria.length) return null;
    const byKey = {};
    allCriteria.forEach((c) => {
      if (!byKey[c.key]) byKey[c.key] = { label: c.label, scores: [] };
      byKey[c.key].scores.push(c.score ?? 0);
    });
    return Object.entries(byKey)
      .map(([key, { label, scores }]) => ({
        key, label,
        avg: (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1),
        count: scores.length,
      }))
      .sort((a, b) => b.avg - a.avg);
  })();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={40} sx={{ color: '#7C3AED' }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* En-tête */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">Évaluations / Bilans</Typography>
          <Typography variant="body2" color="text.secondary">
            {evaluations.length} bilan{evaluations.length !== 1 ? 's' : ''} au total
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/student-evolutions/${profileId}/evaluations/new`)}
          sx={{ backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' } }}
        >
          Nouveau bilan
        </Button>
      </Box>

      {/* Filtre période */}
      {evaluations.length > 0 && (
        <TextField
          select
          label="Période"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          size="small"
          sx={{ minWidth: 220, mb: 3 }}
        >
          {PERIOD_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>
      )}

      {/* Synthèse si 2+ évaluations */}
      {globalStats && filtered.length >= 2 && (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#F5F3FF', borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ color: '#7C3AED', mb: 1.5 }}>
            Moyennes sur {filtered.length} bilans
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1 }}>
            {globalStats.map((s) => (
              <Box key={s.key} sx={{ textAlign: 'center', p: 1, bgcolor: 'white', borderRadius: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                  {s.label}
                </Typography>
                <Typography sx={{ fontWeight: 'bold', fontSize: 18, color: SCORE_HEX(Number(s.avg)) }}>
                  {s.avg}
                </Typography>
                <Typography variant="caption" color="text.secondary">/10</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Liste */}
      {filtered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
          <Typography>
            {evaluations.length === 0
              ? 'Aucun bilan pour le moment. Crée le premier !'
              : 'Aucun bilan sur cette période.'}
          </Typography>
        </Box>
      ) : (
        filtered.map((evaluation) => (
          <EvaluationCard
            key={evaluation.id}
            evaluation={evaluation}
            profileId={profileId}
            onDelete={setDeleteTarget}
          />
        ))
      )}

      {/* Dialog suppression */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Supprimer le bilan{' '}
            <strong>"{deleteTarget?.title || moment(deleteTarget?.date?.toDate?.()).format('DD MMMM YYYY')}"</strong> ?
            Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Annuler</Button>
          <Button onClick={handleDelete} color="error" autoFocus>Supprimer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EvaluationList;